import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, File, Plus, Trash, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ROLE_TOOLS, sameTools, TOOL_CALLS_DEFAULT, TOOL_CALLS_MAX, TOOL_DEFS, TOOL_KEYS, type AgentConfigDTO, type AgentRole, type AgentTemplateDTO, type Effort, type ModelDTO, type Permission, type ToolKey } from '@harness/shared';
import { Modal, PageHeader, Seg, Spinner, Switch, SwitchRow } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { relTime } from '../../lib/format';
import { qk } from '../../lib/keys';
import { useAgents, useModels, type ProjectDetail } from '../../lib/queries';
import { toast } from '../../lib/store';

interface Version {
  version: number;
  note: string;
  createdAt: string;
  config: Partial<AgentConfigDTO>;
}

const ROLES: AgentRole[] = ['Planner', 'Coder', 'Reviewer', 'Researcher', 'Wireframer', 'Analyst', 'Custom'];
const PERMS: { value: Permission; label: string }[] = [
  { value: 'allow', label: 'Always allow' },
  { value: 'ask', label: 'Ask' },
  { value: 'deny', label: 'Deny' },
];
const EFFORTS: Effort[] = ['low', 'medium', 'high', 'xhigh', 'max'];

/** Fields the form edits; version / ids aren't part of the dirty check. */
const editable = (a: AgentConfigDTO) => ({
  name: a.name,
  role: a.role,
  isDefault: a.isDefault,
  systemPrompt: a.systemPrompt,
  modelId: a.modelId,
  fallbackModelId: a.fallbackModelId,
  params: a.params,
  tools: a.tools,
  mcpServers: a.mcpServers,
  workingDir: a.workingDir,
  contextFiles: a.contextFiles,
  limits: a.limits,
});

export function modelLabel(m: ModelDTO) {
  return `${m.modelName} — ${m.providerName}${m.selfHosted ? ' (self-hosted)' : ''}`;
}

export function ModelOptions({ models }: { models: ModelDTO[] }) {
  const chat = models.filter((m) => m.capabilities.kind === 'chat');
  const hosted = chat.filter((m) => !m.selfHosted);
  const self = chat.filter((m) => m.selfHosted);
  return (
    <>
      {hosted.length > 0 && (
        <optgroup label="Hosted">
          {hosted.map((m) => (
            <option key={m.id} value={m.id}>
              {modelLabel(m)}
            </option>
          ))}
        </optgroup>
      )}
      {self.length > 0 && (
        <optgroup label="Self-hosted">
          {self.map((m) => (
            <option key={m.id} value={m.id}>
              {modelLabel(m)}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

export default function AgentsPage({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const { data: agents, isLoading } = useAgents(project.id);
  const { data: models = [] } = useModels();
  const [selId, setSelId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, AgentConfigDTO>>({});
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  // Start a new agent from a library agent, or as a copy of the selected one ('').
  const [fromTpl, setFromTpl] = useState('');
  const library = useQuery({ queryKey: ['agent-library'], queryFn: () => api.get<AgentTemplateDTO[]>('/api/agent-library'), enabled: adding });
  const [diffOf, setDiffOf] = useState<Version | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const sel = agents?.find((a) => a.id === selId) ?? agents?.find((a) => a.isDefault) ?? agents?.[0];
  useEffect(() => {
    if (sel && selId !== sel.id) setSelId(sel.id);
  }, [sel, selId]);

  // Drop drafts that match the saved copy after a reload (e.g. saved elsewhere).
  const draft = sel ? (drafts[sel.id] ?? sel) : undefined;
  const dirty = !!(sel && draft && JSON.stringify(editable(draft)) !== JSON.stringify(editable(sel)));

  const versions = useQuery({
    queryKey: qk.agentVersions(sel?.id ?? ''),
    queryFn: () => api.get<Version[]>(`/api/agents/${sel!.id}/versions`),
    enabled: !!sel,
  });

  const update = (patch: Partial<AgentConfigDTO>) => {
    if (!sel || !draft) return;
    setDrafts((d) => ({ ...d, [sel.id]: { ...draft, ...patch } }));
  };
  const setTool = (k: ToolKey, patch: Partial<{ on: boolean; perm: Permission }>) => update({ tools: { ...draft!.tools, [k]: { ...draft!.tools[k], ...patch } } });
  // A role comes with its tools (a Coder can write), unless the user has already tuned them by hand.
  const changeRole = (role: AgentRole) => {
    if (!draft) return;
    const untouched = sameTools(draft.tools, ROLE_TOOLS[draft.role]) || sameTools(draft.tools, ROLE_TOOLS.Custom);
    if (!untouched || sameTools(draft.tools, ROLE_TOOLS[role])) return update({ role });
    update({ role, tools: ROLE_TOOLS[role] });
    toast(`Tools set for the ${role} role${role === 'Coder' ? ': file write, shell and git now ask for approval' : ''}`);
  };
  const setParam = <K extends keyof AgentConfigDTO['params']>(k: K, v: AgentConfigDTO['params'][K]) => update({ params: { ...draft!.params, [k]: v } });

  const primary = models.find((m) => m.id === draft?.modelId);
  const caps = primary?.capabilities;

  const save = async () => {
    if (!sel || !draft) return;
    setSaving(true);
    try {
      const saved = await api.put<AgentConfigDTO>(`/api/agents/${sel.id}`, { ...editable(draft), expectedVersion: sel.version });
      setDrafts((d) => {
        const { [sel.id]: _drop, ...rest } = d;
        return rest;
      });
      await qc.invalidateQueries({ queryKey: qk.agents(project.id) });
      await qc.invalidateQueries({ queryKey: qk.agentVersions(sel.id) });
      await qc.invalidateQueries({ queryKey: qk.projects });
      toast(`${saved.name} saved as v${saved.version} · applies from the next turn`);
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!sel) return;
    setDrafts((d) => {
      const { [sel.id]: _drop, ...rest } = d;
      return rest;
    });
  };

  const add = async () => {
    const tpl = library.data?.find((t) => t.id === fromTpl);
    const name = newName.trim() || tpl?.name || '';
    if (!name) return;
    try {
      const a = await api.post<AgentConfigDTO>(
        `/api/projects/${project.id}/agents`,
        tpl ? { name, fromTemplateId: tpl.id } : { name, role: 'Custom', cloneFrom: sel?.id },
      );
      await qc.invalidateQueries({ queryKey: qk.agents(project.id) });
      setSelId(a.id);
      setAdding(false);
      setNewName('');
      setFromTpl('');
      toast(`${a.name} added · called with @${a.handle}`);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const restore = async (v: Version) => {
    if (!sel) return;
    try {
      const a = await api.post<AgentConfigDTO>(`/api/agents/${sel.id}/versions/${v.version}/restore`);
      discard();
      setDiffOf(null);
      await qc.invalidateQueries({ queryKey: qk.agents(project.id) });
      await qc.invalidateQueries({ queryKey: qk.agentVersions(sel.id) });
      toast(`${a.name} rolled back to v${v.version} · saved as v${a.version}`);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const remove = async () => {
    if (!sel) return;
    try {
      await api.del(`/api/agents/${sel.id}`);
      setConfirmDelete(false);
      setSelId(null);
      await qc.invalidateQueries({ queryKey: qk.agents(project.id) });
      toast(`${sel.name} removed`);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  if (isLoading || !agents)
    return (
      <div className="settings-content">
        <Spinner />
      </div>
    );

  return (
    <div className="settings-content">
      <PageHeader title="Agents" sub="Each agent has its own model, prompt, tools and permissions. They share history and memory." />

      <div className="ruled-grid agent-cards">
        {agents.map((a) => {
          const d = drafts[a.id] ?? a;
          const m = models.find((x) => x.id === d.modelId);
          return (
            <button key={a.id} className="agent-card" aria-pressed={a.id === sel?.id} onClick={() => setSelId(a.id)}>
              <span className="row" style={{ gap: 8 }}>
                <span className="n">{d.name}</span>
                {a.isDefault && <span className="badge">Default</span>}
                {drafts[a.id] && JSON.stringify(editable(drafts[a.id]!)) !== JSON.stringify(editable(a)) && <span style={{ fontSize: 11, opacity: 0.8 }}>· edited</span>}
              </span>
              <span className="ellipsis" style={{ fontSize: 12, opacity: 0.85 }}>
                {m?.modelName ?? 'No model'}
              </span>
              <span style={{ fontSize: 11, opacity: 0.7 }}>
                v{a.version} · {TOOL_KEYS.filter((k) => a.tools[k].on).length} tools on · @{a.handle}
              </span>
            </button>
          );
        })}
        {adding ? (
          <form
            className="agent-add"
            style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'default' }}
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
          >
            <select className="input" value={fromTpl} onChange={(e) => setFromTpl(e.target.value)} aria-label="Start from">
              <option value="">Copy of {sel?.name ?? 'the selected agent'}</option>
              {(library.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  Library · {t.name}
                </option>
              ))}
            </select>
            <input
              className="input"
              autoFocus
              placeholder={library.data?.find((t) => t.id === fromTpl)?.name ?? 'Agent name'}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              aria-label="New agent name"
              onKeyDown={(e) => e.key === 'Escape' && setAdding(false)}
            />
            <div className="row">
              <button className="btn btn-primary" type="submit" disabled={!newName.trim() && !fromTpl} style={{ padding: '5px 10px' }}>
                Add
              </button>
              <button className="btn btn-ghost" type="button" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button className="agent-add" onClick={() => setAdding(true)}>
            <Plus className="icon" />
            Add agent
          </button>
        )}
      </div>

      {sel && draft && (
        <div className="agent-layout">
          <div className="card agent-form stack">
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 16, alignItems: 'end' }}>
              <div className="field">
                <label htmlFor="a-name">Name</label>
                <input id="a-name" className="input" value={draft.name} onChange={(e) => update({ name: e.target.value })} />
              </div>
              <div className="field">
                <span className="label">Role</span>
                <Seg<AgentRole> value={draft.role} onChange={changeRole} options={ROLES.map((r) => ({ value: r, label: r }))} />
              </div>
            </div>
            <SwitchRow on={draft.isDefault} onChange={(v) => update({ isDefault: v })} disabled={sel.isDefault}>
              Default agent — answers new conversations{sel.isDefault ? ' (make another agent the default to change this)' : ''}
            </SwitchRow>

            <div className="field">
              <label htmlFor="a-prompt">System prompt · variables: {'{{project.name}} {{server.cwd}} {{date}}'}</label>
              <textarea id="a-prompt" className="input mono" style={{ minHeight: 150, lineHeight: 1.6 }} value={draft.systemPrompt} onChange={(e) => update({ systemPrompt: e.target.value })} />
            </div>

            <div className="grid-2">
              <div className="field">
                <label htmlFor="a-model">Primary model</label>
                <select id="a-model" className="input" value={draft.modelId ?? ''} onChange={(e) => update({ modelId: e.target.value || null })}>
                  {!draft.modelId && <option value="">Pick a model…</option>}
                  <ModelOptions models={models} />
                </select>
                {models.length === 0 && <div className="hint">No models yet — add a provider in Providers & models.</div>}
              </div>
              <div className="field">
                <label htmlFor="a-fallback">Fallback model (on error or timeout)</label>
                <select id="a-fallback" className="input" value={draft.fallbackModelId ?? ''} onChange={(e) => update({ fallbackModelId: e.target.value || null })}>
                  <option value="">None</option>
                  <ModelOptions models={models.filter((m) => m.id !== draft.modelId)} />
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 16, alignItems: 'end' }}>
              {caps?.temperature ? (
                <div className="field">
                  <label htmlFor="a-temp">Temperature · {draft.params.temperature ?? 'default'}</label>
                  <div className="row" style={{ flexWrap: 'nowrap' }}>
                    <input id="a-temp" type="range" min={0} max={1} step={0.05} value={draft.params.temperature ?? 1} onChange={(e) => setParam('temperature', parseFloat(e.target.value))} />
                    {draft.params.temperature != null && (
                      <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setParam('temperature', null)}>
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="field">
                  <span className="label">Temperature</span>
                  <div className="hint" style={{ marginTop: 8 }}>
                    {primary ? `Not supported by ${primary.modelName}` : 'Pick a model first'}
                  </div>
                </div>
              )}
              <div className="field">
                <label htmlFor="a-maxtok">Max output tokens</label>
                <input
                  id="a-maxtok"
                  className="input"
                  inputMode="numeric"
                  placeholder={primary ? `default (${Math.min(primary.maxOutput, 64000).toLocaleString()})` : 'provider default'}
                  value={draft.params.maxOutputTokens ?? ''}
                  onChange={(e) => setParam('maxOutputTokens', e.target.value.replace(/\D/g, '') ? Number(e.target.value.replace(/\D/g, '')) : null)}
                />
              </div>
              {caps?.reasoning === 'budget' ? (
                <div className="field">
                  <label htmlFor="a-think">Thinking budget</label>
                  <input
                    id="a-think"
                    className="input"
                    inputMode="numeric"
                    placeholder="off (min 1024)"
                    value={draft.params.thinkingBudget ?? ''}
                    onChange={(e) => setParam('thinkingBudget', e.target.value.replace(/\D/g, '') ? Number(e.target.value.replace(/\D/g, '')) : null)}
                  />
                </div>
              ) : (
                <div className="field">
                  <label htmlFor="a-effort">Reasoning effort</label>
                  <select id="a-effort" className="input" value={draft.params.effort ?? ''} disabled={caps?.reasoning === 'none'} onChange={(e) => setParam('effort', (e.target.value || null) as Effort | null)}>
                    <option value="">{caps?.reasoning === 'none' ? 'Not supported' : 'Model default'}</option>
                    {caps?.reasoning !== 'none' && EFFORTS.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div>
              <div className="section-title">Tools and permissions</div>
              <div className="tool-list">
              {TOOL_DEFS.map((d) => {
                const t = draft.tools[d.key];
                const webNote = d.key === 'web' && primary && primary.adapter !== 'anthropic';
                return (
                  <div key={d.key} className="tool-row">
                    <Switch on={t.on} onChange={(on) => setTool(d.key, { on })} label={`Enable ${d.label}`} />
                    <div style={{ opacity: t.on ? 1 : 0.45 }}>
                      <div className="tool-label">{d.label}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {d.desc}
                        {webNote && <span className="err"> · Hosted web search needs an Anthropic model</span>}
                      </div>
                    </div>
                    <div style={{ opacity: t.on ? 1 : 0.45 }}>
                      <Seg<Permission>
                        size="sm"
                        value={t.perm}
                        onChange={(perm) => setTool(d.key, { perm })}
                        options={PERMS}
                        tone={(v) => (v === 'ask' ? 'accent' : v === 'deny' ? 'muted-fill' : '')}
                      />
                    </div>
                  </div>
                );
              })}
              </div>
            </div>

            <div className="grid-2">
              <div className="field">
                <label htmlFor="a-wd">Working directory</label>
                <input id="a-wd" className="input mono" placeholder={project.server?.workingDir ?? 'server root'} value={draft.workingDir} onChange={(e) => update({ workingDir: e.target.value })} />
                <div className="hint">Empty = the server’s configured root. The agent can’t leave it.</div>
              </div>
              <div className="field">
                <span className="label">MCP servers</span>
                <McpEditor value={draft.mcpServers} onChange={(mcpServers) => update({ mcpServers })} />
              </div>
            </div>

            <div className="field">
              <span className="label">Context files (always injected)</span>
              <ContextFiles value={draft.contextFiles} onChange={(contextFiles) => update({ contextFiles })} />
            </div>

            <div className="grid-2">
              <div className="field">
                <label htmlFor="a-calls">Max tool calls per turn</label>
                <input
                  id="a-calls"
                  className="input"
                  inputMode="numeric"
                  value={draft.limits.maxToolCalls}
                  onChange={(e) => update({ limits: { ...draft.limits, maxToolCalls: Math.min(TOOL_CALLS_MAX, Math.max(1, Number(e.target.value.replace(/\D/g, '')) || 1)) } })}
                />
                <div className="hint">
                  Stops runaway loops. Default {TOOL_CALLS_DEFAULT}, up to {TOOL_CALLS_MAX}.
                </div>
              </div>
              <div className="field">
                <label htmlFor="a-spend">Max spend per conversation</label>
                <input
                  id="a-spend"
                  className="input"
                  placeholder="No cap"
                  value={draft.limits.maxSpendUsd == null ? '' : `$${draft.limits.maxSpendUsd}`}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d.]/g, '');
                    update({ limits: { ...draft.limits, maxSpendUsd: v && Number(v) > 0 ? Number(v) : null } });
                  }}
                />
              </div>
            </div>

            {agents.length > 1 && (
              <div style={{ paddingTop: 8 }}>
                <button className="btn btn-secondary btn-danger" onClick={() => setConfirmDelete(true)}>
                  <Trash className="icon" />
                  Remove {sel.name}
                </button>
              </div>
            )}
          </div>

          <aside className="agent-side">
            <div className="card save-box">
              <div className="micro" style={{ fontSize: 11 }}>
                {dirty ? 'Unsaved changes' : `Saved · v${sel.version}`}
              </div>
              <button className="btn btn-primary btn-block" disabled={!dirty || saving || !draft.name.trim()} onClick={save}>
                {saving ? <Spinner /> : <Check className="icon" />}
                Save as v{sel.version + 1}
              </button>
              {dirty && (
                <button className="btn btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={discard}>
                  Discard changes
                </button>
              )}
            </div>
            <div className="card card-pad versions">
              <div className="section-title" style={{ fontSize: 16, paddingBottom: 6 }}>
                Version history
              </div>
              {versions.isLoading && (
                <div style={{ padding: 10 }}>
                  <Spinner />
                </div>
              )}
              {(versions.data ?? []).slice(0, 12).map((v) => (
                <div key={`${v.version}-${v.createdAt}`} className="version-row">
                  <span className="tag tag-neutral" style={{ justifyContent: 'center' }}>v{v.version}</span>
                  <span>
                    <span style={{ display: 'block', fontWeight: 700 }}>{v.note}</span>
                    <span className="faint" style={{ fontSize: 11 }}>
                      {relTime(v.createdAt)}
                    </span>
                  </span>
                  {v.version !== sel.version ? (
                    <button className="btn btn-ghost" style={{ fontSize: 12, padding: '2px 4px' }} onClick={() => setDiffOf(v)}>
                      Diff
                    </button>
                  ) : (
                    <span className="faint" style={{ fontSize: 11 }}>
                      current
                    </span>
                  )}
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}

      {diffOf && sel && (
        <Modal open onClose={() => setDiffOf(null)} label="Version diff" width={760}>
          <div className="kicker">
            {sel.name} · v{diffOf.version} → v{sel.version} (current)
          </div>
          <div className="dialog-title">What changed since v{diffOf.version}</div>
          <VersionDiff from={diffOf.config} to={sel} models={models} />
          <div className="dialog-actions">
            <button className="btn btn-ghost" onClick={() => setDiffOf(null)}>
              Close
            </button>
            <button className="btn btn-primary" onClick={() => restore(diffOf)}>
              Restore v{diffOf.version}
            </button>
          </div>
        </Modal>
      )}

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} label="Remove agent">
        <div className="dialog-title">Remove {sel?.name}?</div>
        <div className="dialog-body">Past messages keep its name. {sel?.isDefault ? 'Another agent becomes the default.' : ''}</div>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={remove}>
            Remove agent
          </button>
        </div>
      </Modal>
    </div>
  );
}

function McpEditor({ value, onChange }: { value: { name: string; url: string }[]; onChange: (v: { name: string; url: string }[]) => void }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const add = () => {
    try {
      const u = new URL(url.trim());
      onChange([...value, { name: u.hostname.split('.')[0] || u.hostname, url: u.toString() }]);
      setUrl('');
      setOpen(false);
      setErr('');
    } catch {
      setErr('Enter a full URL, e.g. https://mcp.example.com/sse');
    }
  };
  return (
    <div>
      <div className="tag-list">
        {value.map((m, i) => (
          <span key={m.url} className="tag tag-neutral mono" title={m.url}>
            {m.name} · {new URL(m.url).host}
            <button className="x" aria-label={`Remove ${m.name}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
              <X className="icon" style={{ fontSize: 11 }} />
            </button>
          </span>
        ))}
        {!open && (
          <button className="btn btn-ghost accent btn-sm" onClick={() => setOpen(true)}>
            + Add by URL
          </button>
        )}
      </div>
      {open && (
        <div className="row" style={{ marginTop: 6, flexWrap: 'nowrap' }}>
          <input
            className="input mono"
            autoFocus
            placeholder="https://mcp.example.com/sse"
            value={url}
            aria-label="MCP server URL"
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add();
              if (e.key === 'Escape') setOpen(false);
            }}
          />
          <button className="btn btn-secondary" onClick={add}>
            Add
          </button>
        </div>
      )}
      {err && <div className="err">{err}</div>}
      <div className="hint">Saved with the agent. MCP tool calls are wired up in roadmap phase 3.</div>
    </div>
  );
}

function ContextFiles({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [v, setV] = useState('');
  const add = () => {
    const f = v.trim().replace(/^\.\//, '');
    if (f && !value.includes(f)) onChange([...value, f]);
    setV('');
  };
  return (
    <div className="tag-list">
      {value.map((f) => (
        <span key={f} className="tag tag-outline mono" style={{ gap: 6 }}>
          <File className="icon" style={{ fontSize: 12 }} />
          {f}
          <button className="x" aria-label={`Remove ${f}`} onClick={() => onChange(value.filter((x) => x !== f))}>
            <X className="icon" style={{ fontSize: 11 }} />
          </button>
        </span>
      ))}
      <input
        className="input mono"
        style={{ width: 220, minHeight: 28, padding: '3px 8px' }}
        placeholder="+ path, e.g. docs/architecture.md"
        aria-label="Add context file"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
      />
    </div>
  );
}

/** Classic LCS line diff; prompts are short so O(n·m) is fine. */
function lineDiff(a: string, b: string) {
  const x = a.split('\n');
  const y = b.split('\n');
  const dp = Array.from({ length: x.length + 1 }, () => new Array<number>(y.length + 1).fill(0));
  for (let i = x.length - 1; i >= 0; i--) for (let j = y.length - 1; j >= 0; j--) dp[i]![j] = x[i] === y[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
  const out: { t: ' ' | '+' | '-'; s: string }[] = [];
  let i = 0;
  let j = 0;
  while (i < x.length && j < y.length) {
    if (x[i] === y[j]) out.push({ t: ' ', s: x[i++]! }), j++;
    else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) out.push({ t: '-', s: x[i++]! });
    else out.push({ t: '+', s: y[j++]! });
  }
  while (i < x.length) out.push({ t: '-', s: x[i++]! });
  while (j < y.length) out.push({ t: '+', s: y[j++]! });
  return out;
}

function VersionDiff({ from, to, models }: { from: Partial<AgentConfigDTO>; to: AgentConfigDTO; models: ModelDTO[] }) {
  const rows = useMemo(() => {
    const name = (id: string | null | undefined) => (id ? (models.find((m) => m.id === id)?.modelName ?? 'removed model') : 'None');
    const perm = (t?: { on: boolean; perm: Permission }) => (!t ? '—' : !t.on ? 'off' : t.perm === 'allow' ? 'Always allow' : t.perm === 'ask' ? 'Ask' : 'Deny');
    const fmt = (v: unknown) => (v == null || (Array.isArray(v) && v.length === 0) ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
    const r: { label: string; a: string; b: string }[] = [];
    const push = (label: string, a: string, b: string) => a !== b && r.push({ label, a, b });
    push('Name', fmt(from.name), to.name);
    push('Role', fmt(from.role), to.role);
    push('Default', fmt(from.isDefault), String(to.isDefault));
    push('Primary model', name(from.modelId), name(to.modelId));
    push('Fallback model', name(from.fallbackModelId), name(to.fallbackModelId));
    if (from.params) for (const k of Object.keys(to.params) as (keyof AgentConfigDTO['params'])[]) push(`Param · ${k}`, fmt(from.params[k]), fmt(to.params[k]));
    for (const d of TOOL_DEFS) push(d.label, perm(from.tools?.[d.key]), perm(to.tools[d.key]));
    push('Working dir', fmt(from.workingDir), fmt(to.workingDir));
    push('Context files', fmt(from.contextFiles?.join(', ')), fmt(to.contextFiles.join(', ')));
    push('MCP servers', fmt(from.mcpServers?.map((m) => m.url).join(', ')), fmt(to.mcpServers.map((m) => m.url).join(', ')));
    push('Max tool calls', fmt(from.limits?.maxToolCalls), fmt(to.limits.maxToolCalls));
    push('Max spend', fmt(from.limits?.maxSpendUsd), fmt(to.limits.maxSpendUsd));
    return r;
  }, [from, to, models]);
  const prompt = (from.systemPrompt ?? '') !== to.systemPrompt ? lineDiff(from.systemPrompt ?? '', to.systemPrompt) : null;
  return (
    <div className="vdiff-list">
      {rows.length === 0 && !prompt && <div className="empty">No differences.</div>}
      {rows.map((r) => (
        <div key={r.label} className="vdiff-field">
          <span className="muted">{r.label}</span>
          <span className="vdiff-line del">{r.a}</span>
          <span className="vdiff-line add">{r.b}</span>
        </div>
      ))}
      {prompt && (
        <div style={{ padding: '8px 0' }}>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>
            System prompt
          </div>
          {prompt.map((l, i) => (
            <div key={i} className={`vdiff-line ${l.t === '+' ? 'add' : l.t === '-' ? 'del' : ''}`}>
              {l.t} {l.s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
